import commonGlobals from '@opendoor/shared-fe/commonGlobals';
import { getRailsEnv } from '@opendoor/shared-fe/railsEnv';

const railsEnv = getRailsEnv();
const secrets = commonGlobals[railsEnv];

export const GOOGLE_CLIENT_ID = secrets.googleClientID;
export const GOOGLE_MAPS_KEY = secrets.googleMaps;

export const SUPPORTED_LOCALES = ['en-US', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Gets the current environment in a framework-agnostic way.
 * Works with Next.js (Cosmos), Remix (Cake), and browser contexts.
 *
 * Priority order:
 * 1. window.ENV?.DEPLOY_ENV (Remix client-side)
 * 2. process.env.NEXT_PUBLIC_DEPLOY_ENV (Next.js client & server)
 * 3. process.env.DEPLOY_ENV (Remix server-side)
 * 4. getRailsEnv() (Fallback to hostname detection)
 */
/* eslint-disable @opendoor/no-direct-env-check -- implementing the helper function */
export const getDeployEnv = (): keyof typeof commonGlobals => {
  // Check window.ENV first (Remix pattern)
  if (typeof window !== 'undefined' && (window as any).ENV?.DEPLOY_ENV) {
    return (window as any).ENV.DEPLOY_ENV as keyof typeof commonGlobals;
  }

  // Check Next.js public env var
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_DEPLOY_ENV) {
    return process.env.NEXT_PUBLIC_DEPLOY_ENV as keyof typeof commonGlobals;
  }

  // Check Remix server env var
  if (typeof process !== 'undefined' && process.env?.DEPLOY_ENV) {
    return process.env.DEPLOY_ENV as keyof typeof commonGlobals;
  }

  // Falls back to Rails env method in shared-fe
  return getRailsEnv();
};
/* eslint-enable @opendoor/no-direct-env-check */

/**
 * Checks if the current deploy environment is production.
 */
export const isProductionEnv = (): boolean => {
  return getDeployEnv() === 'production';
};

/**
 * Checks if the current deploy environment is development.
 */
export const isDevelopmentEnv = (): boolean => {
  return getDeployEnv() === 'development';
};
